// src/component/GroupAccessGuard.js
//
// グループページ（/group/:id, /groups2025/:id, /groups2025hl/:id,
// /groups2025hln/:id, /groups2026/:id）への直打ちアクセスを制御する。
//
// 仕様：
//  - 管理者：どのグループでも閲覧可（リダイレクトしない）
//  - 一般ユーザー：URL の :id が自分の所属グループと一致していなければ、
//    自分のグループへリダイレクト
//  - 該当年度のグループを持たない一般ユーザー：自分が持つ別年度の
//    グループへ（無ければ /home へ）リダイレクト
//
// `period` props で年度（"2024" | "2025h" | "2025hl" | "2025hln" | "2026"）
// を指定する。
//
// リダイレクトはいきなり行わず、「この画面は無効です」の案内画面を一度挟む。
// 遷移先がある場合は 5 秒カウントダウンしてから実際に遷移する。
// （ユーザーが今何が起きているか視認できるようにするため）

import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { db } from "../api/firebase";
import { useAuth } from "../context/auth";
import { is4kiPaidUser } from "./groupActivity2026Audience";

const PERIODS = {
  2024: {
    field: (u) => u?.participatingGroup || null,
    path: (id) => `/group/${id}`,
  },
  "2025h": {
    field: (u) => u?.groups2025 || null,
    path: (id) => `/groups2025/${id}`,
  },
  "2025hl": {
    field: (u) => u?.groups2025SecondHalf || null,
    path: (id) => `/groups2025hl/${id}`,
  },
  "2025hln": {
    field: (u) => u?.groups2025SecondHalf || null,
    path: (id) => `/groups2025hln/${id}`,
  },
  2026: {
    field: (u) => {
      if (!u?.groupActivity2026?.groupId) return null;
      if (!is4kiPaidUser(u)) return null;
      return u.groupActivity2026.groupId;
    },
    path: (id) => `/groups2026/${id}`,
  },
};

const resolveOwnFallbackPath = (userData) => {
  if (!userData) return "/home";
  if (
    userData.groupActivity2026?.groupId &&
    is4kiPaidUser(userData)
  ) {
    return `/groups2026/${userData.groupActivity2026.groupId}`;
  }
  return "/home";
};

function InvalidScreen({ urlId, destination }) {
  const [secondsLeft, setSecondsLeft] = useState(destination ? 5 : null);

  useEffect(() => {
    if (secondsLeft === null) return undefined;
    if (secondsLeft <= 0) return undefined;
    const t = setTimeout(() => setSecondsLeft((s) => (s == null ? null : s - 1)), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft]);

  if (destination && secondsLeft !== null && secondsLeft <= 0) {
    return <Navigate to={destination} replace />;
  }

  return (
    <Box
      sx={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 3,
      }}
    >
      <Stack
        spacing={2}
        alignItems="center"
        sx={{
          maxWidth: 480,
          p: 4,
          borderRadius: 3,
          bgcolor: "#fff",
          border: "1px solid rgba(0,0,0,0.1)",
          boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, color: "#d32f2f" }}>
          この画面は無効です
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.7)" }}>
          アクセスしようとしたグループID：
          <br />
          <code style={{ wordBreak: "break-all" }}>{urlId}</code>
        </Typography>
        {destination && (
          <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.65)" }}>
            <strong>{secondsLeft}</strong> 秒後に <code>{destination}</code> へリダイレクトします。
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export default function GroupAccessGuard({ period, children }) {
  const { id } = useParams();
  const { user } = useAuth() || {};
  const [userData, setUserData] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!user) {
      setUserData(null);
      setLoaded(true);
      return;
    }
    const ref = doc(db, "users", user.uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setUserData(snap.exists() ? snap.data() : null);
        setLoaded(true);
      },
      (err) => {
        console.error("GroupAccessGuard onSnapshot error:", err);
        setUserData(null);
        setLoaded(true);
      }
    );
    return () => unsub();
  }, [user]);

  if (!user) return <Navigate to="/" replace />;

  if (!loaded) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "40vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const isAdmin = !!userData?.isAdmin;
  if (isAdmin) return children;

  const cfg = PERIODS[period];
  if (!cfg) return children;

  const myId = cfg.field(userData);
  // 自分のグループを持っていない → /home へ（5秒待って遷移）
  if (!myId) {
    return <InvalidScreen urlId={id} destination={resolveOwnFallbackPath(userData)} />;
  }
  // URL と自分のグループが一致しない → 自分のグループへ（5秒待って遷移）
  if (String(id) !== String(myId)) {
    return <InvalidScreen urlId={id} destination={cfg.path(myId)} />;
  }
  return children;
}
